import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Zap, Check, Sparkles, ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const packages = [
  {
    id: 'decision_pack',
    label: 'Decision Pack',
    price: '$4.99',
    desc: 'One additional decision simulation',
    features: ['1 simulation credit', 'All decision modes', 'Scenario comparison', 'PDF report'],
  },
  {
    id: 'decision_sprint',
    label: 'Decision Sprint',
    price: '$14.99',
    desc: 'Four simulations to use when you need them',
    features: ['4 simulation credits', '25% less per credit than a single pack', 'All decision modes', 'PDF reports'],
    popular: true,
  },
];

export default function UpgradePage() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [loadingPkg, setLoadingPkg] = useState(null);

  const handleCheckout = async (packageId) => {
    setLoadingPkg(packageId);
    try {
      const response = await axios.post(
        `${API}/payments/create-checkout`,
        { package_id: packageId, origin_url: window.location.origin },
        { headers: getAuthHeaders(), withCredentials: true },
      );
      if (!response.data?.url) throw new Error('Checkout URL missing');
      window.location.assign(response.data.url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout is temporarily unavailable. Please try again.');
      setLoadingPkg(null);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#050505' }}>
      <nav className="glass-heavy sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#6366F1' }}>
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-lg tracking-tight" style={{ fontFamily: 'Syne' }}>Regret Engine</span>
          </div>
          <Button data-testid="back-to-dashboard" onClick={() => navigate('/dashboard')} variant="ghost" className="text-sm">
            <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: 'rgba(99,102,241,0.15)' }}>
            <Sparkles className="w-4 h-4" style={{ color: '#6366F1' }} />
            <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#6366F1' }}>Decision credits</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Syne' }} data-testid="upgrade-title">
            Pay for the decision, not another subscription
          </h1>
          <p className="text-sm max-w-xl mx-auto" style={{ color: '#94A3B8' }}>
            Your first simulation is free. Add credits only when another important decision needs a careful rehearsal.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {packages.map((pkg) => (
            <div key={pkg.id} className="glass-card rounded-xl p-8 relative overflow-hidden"
              style={pkg.popular ? { border: '1px solid rgba(99,102,241,0.3)' } : {}}
              data-testid={`package-${pkg.id}`}>
              {pkg.popular && (
                <div className="absolute top-0 right-0 px-3 py-1 text-xs font-semibold rounded-bl-lg" style={{ background: '#6366F1' }}>For several decisions</div>
              )}
              <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: 'Syne' }}>{pkg.label}</h3>
              <div className="text-3xl font-bold mb-2" style={{ fontFamily: 'Syne' }}>{pkg.price}</div>
              <p className="text-xs mb-6" style={{ color: '#94A3B8' }}>{pkg.desc}</p>
              <ul className="space-y-3 mb-8">
                {pkg.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-2 text-sm" style={{ color: '#94A3B8' }}>
                    <Check className="w-4 h-4 shrink-0" style={{ color: '#10B981' }} />
                    {feature}
                  </li>
                ))}
              </ul>
              <Button data-testid={`checkout-${pkg.id}`} onClick={() => handleCheckout(pkg.id)} disabled={loadingPkg !== null}
                className="w-full rounded-lg py-5 text-sm font-semibold"
                style={pkg.popular ? { background: '#6366F1', boxShadow: '0 0 15px rgba(99,102,241,0.3)' } : { background: 'rgba(255,255,255,0.1)' }}>
                {loadingPkg === pkg.id ? 'Opening secure checkout…' : `Buy ${pkg.label}`}
              </Button>
            </div>
          ))}
        </div>

        <div className="mt-8 flex items-start justify-center gap-3 text-xs max-w-xl mx-auto" style={{ color: '#64748B' }}>
          <ShieldCheck className="w-4 h-4 shrink-0" style={{ color: '#10B981' }} />
          <p>One-time payment through Dodo Payments. Prices are set in USD; local currency and eligible Indian payment methods can be shown at checkout when Adaptive Pricing is enabled.</p>
        </div>
      </div>
    </div>
  );
}
